// apps/web/src/features/cargo/components/CargoFiltersPanel.tsx
//
// Filters panel (#1231), collapsed by default. Filter axes:
//   Status · Responsible manager · Carrier · Route (pathFormat) · Client
//   payment status · Carrier payment status · Weight/volume/total-cost ranges ·
//   Date type · Date from/to · Overdue. The Route filter (#2165) is fed by the
//   `/cargo/filter-options` `pathFormats` list. All option labels flow through
//   i18n / the shared `*_LABELS` records.

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CARGO_CARRIER_PAYMENT_STATUSES,
  CARGO_CLIENT_PAYMENT_STATUSES,
  CARGO_CARRIER_PAYMENT_STATUS_LABELS,
  CARGO_CLIENT_PAYMENT_STATUS_LABELS,
  type CargoCarrierPaymentStatus,
  type CargoClientPaymentStatus,
  type CargoDateType,
  type CargoLifecycle,
  type CargoOverdueBucket,
} from '@magimp/shared';

import type { UseCargoFiltersResult } from '../useCargoFilters.js';
import { Button } from '../../../ui/index.js';

export interface CargoManagerOption {
  id: string;
  name: string;
}

export interface CargoPathFormatOption {
  value: string;
  count: number;
}

const DATE_TYPES: readonly CargoDateType[] = ['china_ship', 'arrival', 'issue'];
const OVERDUE_BUCKETS: readonly CargoOverdueBucket[] = ['1plus', '7plus'];
const LIFECYCLES: readonly CargoLifecycle[] = ['in_transit', 'arrived_not_issued', 'issued'];

export function CargoFiltersPanel({
  filters,
  managerOptions,
  pathFormatOptions,
}: {
  filters: UseCargoFiltersResult;
  managerOptions: readonly CargoManagerOption[];
  pathFormatOptions: readonly CargoPathFormatOption[];
}) {
  const { t } = useTranslation(['cargo']);

  // Keep the selected route visible even when the scoped options omit it: the
  // backend strips the currently-selected pathFormat from its own option list
  // (D-5), so re-append it locally (FR-6).
  const routeValues = pathFormatOptions.map((o) => o.value);
  const pathFormatChoices =
    filters.pathFormat && !routeValues.includes(filters.pathFormat)
      ? [filters.pathFormat, ...routeValues]
      : routeValues;

  return (
    <div
      data-testid="cargo-filters-panel"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        padding: '12px 24px',
        borderBottom: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
    >
      <Field label={t('cargo:filters.status')}>
        <select
          data-testid="cargo-filter-lifecycle"
          value={filters.lifecycle ?? ''}
          onChange={(e) => filters.setLifecycle((e.target.value || null) as CargoLifecycle | null)}
          style={selectStyle}
        >
          <option value="">{t('cargo:filters.all')}</option>
          {LIFECYCLES.map((l) => (
            <option key={l} value={l}>
              {t(`cargo:lifecycle.${l}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('cargo:filters.manager')}>
        <select
          data-testid="cargo-filter-manager"
          value={filters.responsibleManagerId}
          onChange={(e) => filters.setResponsibleManagerId(e.target.value)}
          style={selectStyle}
        >
          <option value="">{t('cargo:filters.all')}</option>
          {managerOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('cargo:filters.carrier')}>
        {/* Free-text exact-match carrier filter (#1945): the `carrier` param is
            now an open string, not a closed enum, so this is a plain text input
            rather than a dictionary fetch — a manager without `carriers.read`
            can still type a carrier name. Empty input clears the filter. */}
        <input
          type="text"
          data-testid="cargo-filter-carrier"
          value={filters.carrier ?? ''}
          placeholder={t('cargo:filters.carrierPlaceholder')}
          // Keep the raw text so names with internal spaces stay typable; map a
          // blank/whitespace-only value to null (clear). The URL + wire layers
          // trim the canonical value, so no per-keystroke trim is needed here.
          onChange={(e) => filters.setCarrier(e.target.value.trim() === '' ? null : e.target.value)}
          style={selectStyle}
        />
      </Field>

      <Field label={t('cargo:filters.pathFormat')}>
        {/* Exact-match route filter (#2165): the `pathFormat` param is a closed
            select fed by the scoped `/cargo/filter-options` list. Empty clears. */}
        <select
          data-testid="cargo-filter-path-format"
          value={filters.pathFormat ?? ''}
          onChange={(e) => filters.setPathFormat(e.target.value || null)}
          style={selectStyle}
        >
          <option value="">{t('cargo:filters.all')}</option>
          {pathFormatChoices.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('cargo:filters.clientPaymentStatus')}>
        <select
          data-testid="cargo-filter-client-payment"
          value={filters.clientPaymentStatus ?? ''}
          onChange={(e) =>
            filters.setClientPaymentStatus(
              (e.target.value || null) as CargoClientPaymentStatus | null,
            )
          }
          style={selectStyle}
        >
          <option value="">{t('cargo:filters.all')}</option>
          {CARGO_CLIENT_PAYMENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {CARGO_CLIENT_PAYMENT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('cargo:filters.carrierPaymentStatus')}>
        <select
          data-testid="cargo-filter-carrier-payment"
          value={filters.carrierPaymentStatus ?? ''}
          onChange={(e) =>
            filters.setCarrierPaymentStatus(
              (e.target.value || null) as CargoCarrierPaymentStatus | null,
            )
          }
          style={selectStyle}
        >
          <option value="">{t('cargo:filters.all')}</option>
          {CARGO_CARRIER_PAYMENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {CARGO_CARRIER_PAYMENT_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </Field>

      <NumberRangeField
        label={t('cargo:filters.weightRange')}
        fromLabel={t('cargo:filters.rangeFrom')}
        toLabel={t('cargo:filters.rangeTo')}
        from={filters.weightFrom}
        to={filters.weightTo}
        onFromChange={(value) => filters.setRangeBound('weightFrom', value)}
        onToChange={(value) => filters.setRangeBound('weightTo', value)}
        testIdPrefix="cargo-filter-weight"
      />

      <NumberRangeField
        label={t('cargo:filters.volumeRange')}
        fromLabel={t('cargo:filters.rangeFrom')}
        toLabel={t('cargo:filters.rangeTo')}
        from={filters.volumeFrom}
        to={filters.volumeTo}
        onFromChange={(value) => filters.setRangeBound('volumeFrom', value)}
        onToChange={(value) => filters.setRangeBound('volumeTo', value)}
        testIdPrefix="cargo-filter-volume"
      />

      <NumberRangeField
        label={t('cargo:filters.totalCostRange')}
        fromLabel={t('cargo:filters.rangeFrom')}
        toLabel={t('cargo:filters.rangeTo')}
        from={filters.totalCostFrom}
        to={filters.totalCostTo}
        onFromChange={(value) => filters.setRangeBound('totalCostFrom', value)}
        onToChange={(value) => filters.setRangeBound('totalCostTo', value)}
        testIdPrefix="cargo-filter-total-cost"
      />

      <Field label={t('cargo:filters.dateType')}>
        <select
          data-testid="cargo-filter-date-type"
          value={filters.dateType}
          onChange={(e) => filters.setDateType(e.target.value as CargoDateType)}
          style={selectStyle}
        >
          {DATE_TYPES.map((d) => (
            <option key={d} value={d}>
              {t(`cargo:dateTypes.${d}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('cargo:filters.dateFrom')}>
        <input
          type="date"
          data-testid="cargo-filter-date-from"
          value={filters.dateFrom}
          onChange={(e) => filters.setDateFrom(e.target.value)}
          style={selectStyle}
        />
      </Field>

      <Field label={t('cargo:filters.dateTo')}>
        <input
          type="date"
          data-testid="cargo-filter-date-to"
          value={filters.dateTo}
          onChange={(e) => filters.setDateTo(e.target.value)}
          style={selectStyle}
        />
      </Field>

      <Field label={t('cargo:filters.overdue')}>
        <select
          data-testid="cargo-filter-overdue"
          value={filters.overdue ?? ''}
          onChange={(e) =>
            filters.setOverdue((e.target.value || null) as CargoOverdueBucket | null)
          }
          style={selectStyle}
        >
          <option value="">{t('cargo:filters.all')}</option>
          {OVERDUE_BUCKETS.map((o) => (
            <option key={o} value={o}>
              {t(`cargo:overdue.${o}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('cargo:filters.hasRejectedDocument')}>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 'var(--control-h-sm)' }}
        >
          <input
            type="checkbox"
            data-testid="cargo-filter-rejected-document"
            checked={filters.hasRejectedDocument}
            onChange={(e) => filters.setHasRejectedDocument(e.target.checked)}
          />
          <span style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-1)' }}>
            {t('cargo:filters.hasRejectedDocumentHint')}
          </span>
        </label>
      </Field>

      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
        <Button kind="ghost" size="sm" icon="x" onClick={filters.resetFilters}>
          {t('cargo:actions.resetFilters')}
        </Button>
      </div>
    </div>
  );
}

function NumberRangeField({
  label,
  fromLabel,
  toLabel,
  from,
  to,
  onFromChange,
  onToChange,
  testIdPrefix,
}: {
  label: string;
  fromLabel: string;
  toLabel: string;
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  testIdPrefix: string;
}) {
  return (
    <Field label={label}>
      <span style={{ display: 'flex', gap: 'var(--s-2)' }}>
        <input
          type="number"
          min={0}
          step="any"
          aria-label={`${label}: ${fromLabel}`}
          data-testid={`${testIdPrefix}-from`}
          value={from}
          placeholder={fromLabel}
          onChange={(event) => onFromChange(event.target.value)}
          style={{ ...selectStyle, minWidth: 0, width: '50%' }}
        />
        <input
          type="number"
          min={0}
          step="any"
          aria-label={`${label}: ${toLabel}`}
          data-testid={`${testIdPrefix}-to`}
          value={to}
          placeholder={toLabel}
          onChange={(event) => onToChange(event.target.value)}
          style={{ ...selectStyle, minWidth: 0, width: '50%' }}
        />
      </span>
    </Field>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 'var(--fz-11)', color: 'var(--fg-3)' }}>{label}</span>
      {children}
    </label>
  );
}

const selectStyle = {
  height: 'var(--control-h-sm)',
  padding: '0 8px',
  fontSize: 'var(--fz-12)',
  borderRadius: 'var(--r-1)',
  border: '1px solid var(--border-1)',
  background: 'var(--bg-0)',
  color: 'var(--fg-1)',
  fontFamily: 'var(--font-ui)',
} as const;
